import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Request config with the signed-in user's token (read per call, never cached). */
export const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const EMPTY = { data: null, error: null, loading: true };

/**
 * Breach Monitor data. Each source keeps its own { data, error, loading }:
 * a failed source shows an error (with the last good data kept, marked
 * stale) instead of rendering as zero, so a 500 never reads as all-clear.
 */
export function useBreachData() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [domains, setDomains] = useState(EMPTY);
  const [emails, setEmails] = useState(EMPTY);
  const [results, setResults] = useState(EMPTY);
  const [summary, setSummary] = useState(EMPTY);
  const [schedule, setSchedule] = useState(null);
  const seq = useRef(0);

  const reload = useCallback(() => {
    // Only the newest reload applies its responses, so a slow request that
    // started before a mutation can't overwrite the post-mutation state.
    const mine = ++seq.current;
    const sources = [
      ['domains', setDomains],
      ['emails', setEmails],
      ['breach-results', setResults],
      ['breach-summary', setSummary],
    ];
    sources.forEach(([, set]) => set((s) => ({ ...s, loading: true })));
    return Promise.allSettled(sources.map(([path]) => axios.get(`${API}/${path}`, auth()))).then((settled) => {
      if (mine !== seq.current) return;
      if (settled.some((r) => r.status === 'rejected' && r.reason?.response?.status === 401)) {
        navigateRef.current('/login');
        return;
      }
      settled.forEach((r, i) => {
        const set = sources[i][1];
        if (r.status === 'fulfilled') set({ data: r.value.data, error: null, loading: false });
        else set((s) => ({ data: s.data, error: r.reason, loading: false }));
      });
    });
  }, []);

  useEffect(() => {
    reload();
    axios
      .get(`${API}/breach-monitor/schedule`, auth())
      .then((res) => setSchedule(res.data))
      .catch(() => setSchedule(null)); // the sweep line just omits the interval
  }, [reload]);

  return { domains, emails, results, summary, schedule, reload };
}

/** "every hour", "every 2 hours", "every 30 minutes" */
export function describeInterval(seconds) {
  if (!seconds) return null;
  if (seconds % 3600 === 0) {
    const h = seconds / 3600;
    return h === 1 ? 'every hour' : `every ${h} hours`;
  }
  return `every ${Math.round(seconds / 60)} minutes`;
}

export const domainOf = (email) => String(email || '').split('@')[1]?.toLowerCase() || '';
